// Single source of truth for Domain Intelligence severity handling.
//
// This file MIRRORS the engine's `scoop.ai.investigation.Severity` enum
// (scoop repo, app/src/main/java/scoop/ai/investigation/Severity.java) —
// SIX canonical tiers, worst first:
//
//   rank 0  Critical   "Visit this week"
//   rank 1  Severe     "Priority for this quarter"
//   rank 2  Moderate   "Issues to address"
//   rank 3  Watch      "One more month of this means trouble"
//   rank 4  Mild       "Generally stable with minor concerns"
//   rank 5  Healthy    "Stable or growing"
//
// WHY SIX, and why this file changed (Brad's decision, 2026-07-28). The engine
// has always EMITTED six — the report template asks for six, the DOCX legend
// prints six with distinct operator SLAs, EZCorp's QA gate requires all six.
// But several consumers ranked only four, and this file was one of them: it
// treated `critical` as an alias of Severe and `watch` as an alias of Mild.
// That silently collapsed two real tiers. Two customer-visible defects followed
// — Critical sorted BELOW Healthy in the Portal, and Watch rendered unstyled
// grey. The engine's vocabulary is the contract; the lagging consumers come up
// to it rather than the contract coming down.
//
// Note WATCH ranks WORSE than MILD. That is deliberate and comes from the
// legend: Watch is a trajectory signal ("one more month of this means
// trouble"), Mild is a steady-state one ("generally stable").
//
// Every component MUST get severity rank, color, and label from here. The
// 2026-07 audit (F3) found three disagreeing local tables; keeping the table in
// one place is the only thing that stops them growing back.

const LEVELS = {
    critical: { rank: 0, label: 'Critical', color: 'critical', flagged: true },
    severe: { rank: 1, label: 'Severe', color: 'error', flagged: true },
    moderate: { rank: 2, label: 'Moderate', color: 'warning', flagged: true },
    watch: { rank: 3, label: 'Watch', color: 'watch', flagged: true },
    mild: { rank: 4, label: 'Mild', color: 'info', flagged: true },
    healthy: { rank: 5, label: 'Healthy', color: 'success', flagged: false },
}

// Aliases the engine or legacy config may emit. These are NOT tiers — they
// normalize onto one. This table mirrors the engine's ALIASES map exactly, and
// like the engine it deliberately EXCLUDES `critical` and `watch`: those are
// canonical tiers, and aliasing them onto Severe/Mild is precisely the collapse
// this module reverses.
//
// The former `high`/`medium`/`low` entries were removed in the same pass. They
// were never entity-severity values — they belong to a different axis (pattern
// and label severity in context config, e.g. death_spiral_diagnosis.json), no
// consumer of this module ever passes them, and under six tiers their intent is
// ambiguous anyway (is `high` Critical or Severe?). Guessing on their behalf is
// the same silent-collapse pathology in a smaller costume.
const ALIASES = {
    concerning: 'moderate',
    stable: 'healthy',
    'not present': 'healthy',
    not_present: 'healthy',
}

/** Unknown sorts after every known tier — never silently inside the ladder. */
export const UNKNOWN_RANK = Object.keys(LEVELS).length

/** Canonical lowercase key for a severity value, or 'unknown'. */
export const normalizeSeverity = (severity) => {
    const raw = String(severity ?? '')
        .trim()
        .toLowerCase()
    if (!raw) return 'unknown'
    if (LEVELS[raw]) return raw
    if (ALIASES[raw]) return ALIASES[raw]
    return 'unknown'
}

/** 0 (Critical, worst) … 5 (Healthy, best); unknown sorts last, matching the engine. */
export const severityRank = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.rank ?? UNKNOWN_RANK
}

/** Chip/badge color token for any severity value. Unknown gets 'default', never a canonical value. */
export const severityColor = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.color ?? 'default'
}

/** Canonical display label (e.g. 'concerning' -> 'Moderate'). Unknown values echo trimmed input or 'Unknown'. */
export const severityLabel = (severity) => {
    const key = normalizeSeverity(severity)
    if (LEVELS[key]) return LEVELS[key].label
    const raw = String(severity ?? '').trim()
    return raw || 'Unknown'
}

/** Whether this severity alone marks the entity as flagged — i.e. anything but Healthy. */
export const isFlaggedSeverity = (severity) => {
    const key = normalizeSeverity(severity)
    return LEVELS[key]?.flagged ?? false
}

/** True when the value maps to a canonical level (i.e. the engine could have produced it). */
export const isKnownSeverity = (severity) => normalizeSeverity(severity) !== 'unknown'

/** Canonical keys, worst first. Order is load-bearing — segment bars and legends iterate it. */
export const CANONICAL_SEVERITIES = Object.keys(LEVELS)
export const SEVERITY_ALIASES = { ...ALIASES }
